export type ISODateTime = string;

export const itemStatuses = [
  'STATUS_UNSPECIFIED',
  'ORDERED',
  'BUILD_IN_PROGRESS',
  'SHIPPED_TO_WAREHOUSE',
  'DELIVERED_TO_WAREHOUSE',
  'FIELD_VALIDATION',
  'SHIPPED_TO_CUSTOMER',
  'DELIVERED_TO_CUSTOMER',
  'ACTIVE',
  'RMA_IN_PROGRESS',
  'MAINTENANCE_IN_PROGRESS',
  'RETURN_STARTED',
  'RETURN_SHIPPED_TO_WAREHOUSE',
  'RETURN_DELIVERED_TO_WAREHOUSE',
  'IN_WAREHOUSE_STORAGE',
  'NEEDS_REPAIR',
  'SCRAP',
  'READY_TO_SHIP',
  'CLEANING',
  'EOL_TESTING',
  'REPLACED',
] as const;
export type ItemStatus = (typeof itemStatuses)[number];

export const transferReasons = [
  'REASON_UNSPECIFIED',
  'SITE_HARVEST',
  'SCHEDULED_MAINTENANCE',
  'UNIT_DAMAGE',
  'UNIT_MALFUNCTION',
  'OVERSTOCK',
] as const;
export type TransferReason = (typeof transferReasons)[number];

export const partTypes = [
  'PART_TYPE_UNSPECIFIED',
  'CAMERA_SYSTEM',
  'DOCK_BOX',
  'MOUNTING_KIT',
  'RADIO_NANOSTATION',
  'SURFACE_CAMERA',
  'SITE_CONTROLLER',
  'UMBILICAL',
  'UNIT_CONTROLLER',
  'WATER_QUALITY_SENSOR',
  'WINCH_SYSTEM',
  'POWERBOX',
] as const;
export type PartType = (typeof partTypes)[number];

export interface Transfer {
  trackingNumber?: string | undefined;
  reason?: TransferReason | undefined;
  transferTime?: ISODateTime | undefined;
}

export interface Item {
  id: string;
  partId: string;
  siteId: string;
  locationSiteId?: string | undefined;
  location?: string | undefined;
  description?: string | undefined;
  identity?: string | undefined;
  status?: ItemStatus | undefined;
  notes?: string | undefined;
  lastTransfer?: Transfer | undefined;
  inspectedDate?: ISODateTime | undefined;
  inspectedBy?: string | undefined;
  maintenanceDate?: ISODateTime | undefined;
  maintenanceBy?: string | undefined;
  maintenanceNotes?: string | undefined;
  shippingDate?: ISODateTime | undefined;
  installationDate?: ISODateTime | undefined;
  decommissionDate?: ISODateTime | undefined;
  needsAttention?: boolean | undefined;
  attentionReason?: string | undefined;
  hardwareRevision?: string | undefined;
  /** For a REPLACED item: the item that took its site and pen, and since when. */
  replacedByItemId?: string | undefined;
  replacedAt?: ISODateTime | undefined;
  version: number;
  lastModifiedBy?: string | undefined;
  lastModified?: ISODateTime | undefined;
}

// ItemUsage is cached, daily-refreshed device usage telemetry for an
// Item (id === Item.id) -- backed by leatherback's item_usage table,
// itself mirrored from a BigQuery view derived from device heartbeats.
// Not part of Item itself: Items.ListItemUsages/BatchUpdateItemUsages
// are a deliberately separate RPC pair, not fields on Item/ItemPatch.
// See tidaltech/koi#7812.
export interface ItemUsage {
  id: string;
  lastUsed?: string | undefined; // YYYY-MM-DD
  lastSiteId?: string | undefined;
  lastSiteName?: string | undefined;
  lastPen?: string | undefined;
  daysUsed?: number | undefined;
}

export interface Part {
  id: string;
  type: PartType;
  build: string;
  version: number;
  lastModifiedBy?: string | undefined;
  lastModified?: ISODateTime | undefined;
}

export interface Customer {
  id: string;
  name: string;
  displayName?: string | undefined;
  // Omitted from the wire payload when it's the default/unspecified enum
  // value (protobuf JSON drops zero values) — see live-inventory-api.ts.
  customerType?: string | undefined;
  state: string;
  labels?: Record<string, string> | undefined;
  version: number;
  lastModifiedBy?: string | undefined;
  lastModified?: ISODateTime | undefined;
}

// Postal address used when shipping to a site. The country is not part of
// the address; it lives in the site's labels.country.
export interface SiteAddress {
  line1?: string | undefined;
  line2?: string | undefined;
  postcode?: string | undefined;
  city?: string | undefined;
}

export interface Site {
  id: string;
  customerId: string;
  name: string;
  displayName?: string | undefined;
  region?: string | undefined;
  officialSiteId?: string | undefined;
  latitude?: number | undefined;
  longitude?: number | undefined;
  labels?: Record<string, string> | undefined;
  // A site's contact name and phone moved to Contacts (tidaltech/koi#8359).
  address?: SiteAddress | undefined;
  // How many systems the customer ordered for the site (tidaltech/koi#8218).
  orderedSystems?: number | undefined;
  version: number;
  lastModifiedBy?: string | undefined;
  lastModified?: ISODateTime | undefined;
}

export type ClearableSiteField = 'address' | 'orderedSystems';

// UpdateSite only merges populated fields, so clearing a value must be
// requested explicitly through `clear` (UpdateSiteRequest.clear_mask).
export interface SiteContactPatch {
  id: string;
  customerId: string;
  version: number;
  address?: SiteAddress | undefined;
  orderedSystems?: number | undefined;
  clear?: ClearableSiteField[] | undefined;
}

export interface Unit {
  id: string;
  siteId: string;
  displayName?: string | undefined;
  status?: string | undefined;
  waterQualityDelegateUnitName?: string | undefined;
  version: number;
  lastModifiedBy?: string | undefined;
  lastModified?: ISODateTime | undefined;
}

// Matches protos/v1/subscriptions.proto's Feature.Name exactly. There is no
// WELFARE value yet even though at least one customer is billed for it —
// see OPEN_QUESTIONS.md rather than inventing a client-only value.
export const subscriptionFeatures = [
  'FEATURE_UNSPECIFIED',
  'BIOMASS',
  'GROWTH_TRENDS',
  'SEA_LICE',
  'FEEDING_METRICS',
  'FEEDING_INSIGHTS',
  'AUTONOMOUS_FEEDING',
] as const;
export type SubscriptionFeature = (typeof subscriptionFeatures)[number];

export const subscriptionTiers = ['TIER_UNSPECIFIED', 'BASIC'] as const;
export type SubscriptionTier = (typeof subscriptionTiers)[number];

/**
 * A customer's entitlements. This is the API's merged view of two backend
 * resources (Subscription + SubscriptionType) — see
 * docs/decisions/ADR-0007-customer-entitlements-workspace.md. The wire field
 * is `revision`, not `version`, and there is no modifying-actor field exposed
 * by this API at all (unlike Item/Customer/Site), so no lastModifiedBy is
 * modeled here — see OPEN_QUESTIONS.md.
 */
export interface Subscription {
  id: string;
  customerId: string;
  tier?: SubscriptionTier | undefined;
  features: SubscriptionFeature[];
  contractName?: string | undefined;
  contractUrl?: string | undefined;
  notes?: string | undefined;
  numberOfSystems?: number | undefined;
  regions?: string[] | undefined;
  startTime?: ISODateTime | undefined;
  endTime?: ISODateTime | undefined;
  updateTime?: ISODateTime | undefined;
  revision: number;
}

// Sent to UpdateSubscription. `id` identifies the URL path, not the body —
// see live-inventory-api.ts. `revision` is the currently-loaded value the
// server checks for optimistic concurrency, same role as ItemPatch.version.
export type SubscriptionPatch = Partial<
  Pick<
    Subscription,
    | 'contractName'
    | 'contractUrl'
    | 'notes'
    | 'numberOfSystems'
    | 'regions'
    | 'startTime'
    | 'endTime'
  >
> & {
  id: string;
  revision: number;
};

// Sent to CreateSubscription. The service requires start/end time and
// rejects a request that sets features (they're always inherited from the
// customer's existing SubscriptionType) — see services/v1/subscriptions.go.
export type CreateSubscriptionInput = Partial<
  Pick<Subscription, 'contractName' | 'contractUrl' | 'notes' | 'numberOfSystems' | 'regions'>
> & {
  startTime: ISODateTime;
  endTime: ISODateTime;
};

export interface AuditEvent {
  auditId: string;
  recordId: string;
  operationType: string;
  changedAt: ISODateTime;
  changedBy?: string | undefined;
  old?: Record<string, unknown> | null | undefined;
  new?: Record<string, unknown> | null | undefined;
}

export interface AuditFilters {
  query?: string | undefined;
  operation?: string | undefined;
  dateFrom?: ISODateTime | undefined;
  dateTo?: ISODateTime | undefined;
  pageSize?: number | undefined;
  pageToken?: string | undefined;
}

export interface ItemFilters {
  query?: string | undefined;
  customerId?: string | undefined;
  siteId?: string | undefined;
  assignedSiteId?: string | undefined;
  locationSiteId?: string | undefined;
  partId?: string | undefined;
  partIds?: string[] | undefined;
  status?: ItemStatus | undefined;
  statuses?: ItemStatus[] | undefined;
  needsAttention?: boolean | undefined;
  pageSize?: number | undefined;
  pageToken?: string | undefined;
}

export interface Page<T> {
  data: T[];
  nextPageToken?: string | undefined;
  total?: number | undefined;
}

export type ItemPatch = Partial<
  Omit<Item, 'id' | 'version' | 'lastModified' | 'lastModifiedBy'>
> & {
  id: string;
  version: number;
};

export type CreateItemInput = Pick<Item, 'id' | 'partId' | 'siteId'> &
  Partial<Omit<Item, 'id' | 'partId' | 'siteId' | 'version' | 'lastModified' | 'lastModifiedBy'>>;
